import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { date, money } from '../components/format';
import { Alert, Badge, Button, Card, Spinner } from '../components/ui';
import { ApiError, api } from '../services/api';
import { useSession } from '../session';
import type { Invoice } from '../types';

export function InvoicesPage() {
  const { t, i18n } = useTranslation();
  const { user } = useSession();
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [state, setState] = useState<'ok' | 'forbidden' | 'error'>('ok');
  useEffect(() => {
    api
      .invoices()
      .then(setInvoices)
      .catch((err) => setState(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error'));
  }, []);

  const download = async (inv: Invoice) => {
    const blob = await api.invoicePdf(inv.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = inv.pdf_name ?? `${inv.number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (state === 'forbidden') return <Alert kind="info">{t('invoices.adminOnly')}</Alert>;
  if (state === 'error') return <Alert kind="error">{t('app.error')}</Alert>;
  if (!invoices || !user) return <Spinner />;
  return (
    <Card title={t('invoices.title')}>
      {invoices.length === 0 ? (
        <p className="text-[13px] text-faint">{t('invoices.none')}</p>
      ) : (
        <InvoiceTable invoices={invoices} locale={i18n.language} onDownload={(inv) => void download(inv)} />
      )}
    </Card>
  );
}

/** The organization's invoices and credit notes (PLAN-payments.md M2): excl. and incl. VAT, a credit note as a
 *  negative amount naming the invoice it cancels. Staff-recorded invoices of before have no VAT: their amount is
 *  the whole. */
export function InvoiceTable({ invoices, locale, onDownload }: { invoices: Invoice[]; locale: string; onDownload: (inv: Invoice) => void }) {
  const { t } = useTranslation();
  const numbers = new Map(invoices.map((inv) => [inv.id, inv.number]));
  const tone = (s: Invoice['status']) => (s === 'paid' ? 'accent' : s === 'void' ? 'neutral' : 'warn');
  const signed = (inv: Invoice, value: string) => money(inv.kind === 'credit_note' ? -Number(value) : value, inv.currency, locale);
  return (
    <div className="-mx-2 overflow-x-auto">
      <table className="w-full min-w-[600px] text-[13px]">
        <thead>
          <tr className="text-left font-mono text-[10.5px] uppercase tracking-wider text-faint">
            <th className="px-2 py-1.5 font-normal">{t('invoices.number')}</th>
            <th className="px-2 py-1.5 font-normal">{t('invoices.issued')}</th>
            <th className="px-2 py-1.5 font-normal">{t('invoices.due')}</th>
            <th className="px-2 py-1.5 text-right font-normal">{t('invoices.amount')}</th>
            <th className="px-2 py-1.5 text-right font-normal">{t('invoices.total')}</th>
            <th className="px-2 py-1.5 font-normal">{t('invoices.status')}</th>
            <th className="px-2 py-1.5" />
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => {
            const credit = inv.kind === 'credit_note';
            const cancels = credit && inv.credits_invoice_id != null ? numbers.get(inv.credits_invoice_id) : null;
            return (
              <tr key={inv.id} className="border-t border-line-row" data-testid={`invoice-${inv.id}`}>
                <td className="px-2 py-2 font-mono text-ink">
                  {inv.number ?? '—'}
                  <div className="font-sans text-[11.5px] text-faint">
                    {t(`invoices.kind.${inv.kind}`)}
                    {cancels ? ` · ${t('invoices.creditsInvoice', { number: cancels })}` : ''}
                  </div>
                </td>
                <td className="px-2 py-2 text-muted">{date(inv.issued_at, locale)}</td>
                <td className="px-2 py-2 text-muted">{credit ? '—' : date(inv.due_at, locale)}</td>
                <td className="px-2 py-2 text-right font-mono text-ink">{signed(inv, inv.amount)}</td>
                <td className="px-2 py-2 text-right font-mono text-ink">{signed(inv, inv.total ?? inv.amount)}</td>
                <td className="px-2 py-2">
                  {credit ? <Badge tone="neutral">{t('invoices.creditNote')}</Badge> : <Badge tone={tone(inv.status)}>{t(`invoices.statusLabel.${inv.status}`)}</Badge>}
                </td>
                <td className="px-2 py-2 text-right">
                  {inv.has_pdf && (
                    <Button variant="ghost" className="px-2! py-1! text-[12px]" onClick={() => onDownload(inv)}>
                      {t('invoices.download')}
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
